// Time formatting and calculation utilities

/**
 * Converts time string to decimal hours
 * e.g., "2:30PM" -> 14.5, "11:00PM" -> 23.0
 *
 * @param timeStr Time string in format "HH:MM AM/PM" or "HH:MM"
 * @returns Decimal hour representation (0-24)
 */
export function timeStringToDecimalHour(timeStr: string | null | undefined): number {
	// Handle null/undefined for lineup-only festivals
	if (!timeStr) return 0;

	// Handle different time formats
	const match = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
	if (!match) return 0;

	let hours = Number.parseInt(match[1] || "0", 10);
	const minutes = Number.parseInt(match[2] || "0", 10);
	const period = match[3]?.toUpperCase();

	// Convert to 24-hour format
	if (period === "PM" && hours !== 12) {
		hours += 12;
	} else if (period === "AM" && hours === 12) {
		hours = 0;
	}

	return hours + minutes / 60;
}

/**
 * Calculates duration in hours from start and end times
 * Handles midnight crossing (e.g., 11:00 PM - 2:00 AM = 3 hours)
 *
 * @param startTime Start time string (e.g., "11:00 PM")
 * @param endTime End time string (e.g., "2:00 AM")
 * @param scheduleIsOvernight Whether the festival schedule is overnight (optional, for context)
 * @returns Duration in decimal hours
 *
 * @example
 * calculateSetDuration("11:00 PM", "2:00 AM") // 3.0 hours
 * calculateSetDuration("2:00 PM", "4:30 PM")  // 2.5 hours
 * calculateSetDuration(null, null)            // 1.0 hour (default)
 */
export function calculateSetDuration(
	startTime: string | null | undefined,
	endTime: string | null | undefined,
	scheduleIsOvernight?: boolean,
): number {
	// Handle null/undefined for lineup-only festivals
	if (!startTime || !endTime) return 1; // Default 1 hour for lineup-only

	const startHour = timeStringToDecimalHour(startTime);
	const endHour = timeStringToDecimalHour(endTime);

	// Handle midnight crossing:
	// - If endHour < startHour, the set crosses midnight (e.g., 11PM → 2AM)
	// - This applies whether scheduleIsOvernight is true OR when schedule_end_hour = 24
	if (endHour < startHour) {
		return 24 - startHour + endHour;
	}

	return Math.max(0.25, endHour - startHour); // Minimum 15 minutes
}
